import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';

import { Subscription } from 'rxjs';

import { ColumnToNotDisplay } from 'src/app/enums/ColumnsToNotDisplay';
import { EventsUpdateTagGroup } from 'src/app/models/events/eventsUpdateTagGroup';
import { GraphTagGroupListModel } from 'src/app/models/tags/graph-tag-group-list-model';
import { StationsRepo } from 'src/app/repositories/stations/repo/station-repo';
import { AlertService } from 'src/app/services';
import { AlertParameters, AlertType } from 'src/app/shared/models/models-index';
import { TagFilterType } from 'src/app/shared/models/tag/tag-filter-type';
import { GraphTagSearch, TagGroup } from 'src/swagger-gen/web-secured/models';
import { TableColumn } from '../../tinea-table/models/tableColumn';

@Component({
    selector: 'app-tag-group',
    templateUrl: './tag-group.component.html',
    styleUrls: ['./tag-group.component.css'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TagGroupComponent implements OnInit, OnChanges, OnDestroy {
  @Input() group: TagGroup;

  @Input() tableColumns: TableColumn[] = [];

  @Input() data: any;

  @Input() selectedTagsByDefault: string[];

  @Input() columnsToNotDisplay: ColumnToNotDisplay;

  @Output() eventOnClick = new EventEmitter<any>();


  tagsGroupSubscr: Subscription;

  limit = 100;
  offset = 0;

  firstPass = false;

  isDown = true;
  cssClassForCollapse = 'down';
  cssClassForCollapseIcon = 'icon-chevron-down';

  tagGroupType = TagFilterType;

  /**
   * Current application language
   */
  language: 'fr' | 'en' = 'fr';

  /**
   * Date format to display depending of language
   */
  dateFormat: string;

  /**
   * List of grouped tags
   */
  tagsList: GraphTagGroupListModel;

  constructor(
    private stationRepo: StationsRepo,
    private alertService: AlertService
  ) {}

  ngOnDestroy(): void {
    if (!!this.tagsGroupSubscr) {
      this.tagsGroupSubscr.unsubscribe();
    }
  }

  ngOnInit() {
    this.setDateFormat();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.data?.currentValue && changes.data?.firstChange)
        || (changes.group?.currentValue && changes.group?.firstChange)
        || (changes.selectedTagsByDefault?.currentValue && changes.selectedTagsByDefault?.firstChange)) {
      // first call for getting tags
      this.getTagsGroupByField(false);
    }
  }

  /**
   * Set the date format depending on the language
   */
  setDateFormat() {
    if (this.language === 'fr') {
      this.dateFormat = 'dd/MM/yyyy';
    } else {
      this.dateFormat = 'MM/dd/yyyy';
    }
  }

  onClickGroup($tag: any) {
    const element: Element = $tag.target;
    const isDown = element.classList.contains(this.cssClassForCollapse);
    if (!isDown) {
      element.classList.add(this.cssClassForCollapse);
      this.getTagsGroupByField(false);
      this.isDown = false;
    } else {
      element.classList.remove(this.cssClassForCollapse);
      this.tagsList = null;
      this.offset = 0;
      this.isDown = true;
    }
  }

  /**
   * If at least one checkbox is checked in the group, show lines. If not, hide them
   */
  showOrHide() {
    const countSelected: any[] = [];
    // count number of checkboxes' checks by group
    this.selectedTagsByDefault.forEach(tagUid => {
      const selectedTag = this.tagsList.list.find(tag => tag.uid === tagUid);
      if (!!selectedTag) {
        countSelected.push(selectedTag);
      }
    });

    // to show or not to show
    if (countSelected.length > 0) {
      this.isDown = false;
    } else {
      this.isDown = true;
    }
  }

  getTagsGroupByField(pushList: boolean) {
    const params: any[] = this.data.params;
    const body = this.data.body;
    if (this.group.label || this.group.label === '') {
      const exist = params.find((e) => e.key === 'groupValue');
      if (exist) {
        const index = params.findIndex((e) => e.key === 'groupValue');
        if (index !== -1) {
          params.splice(index, 1);
        }
      }
      if (this.group.label !== 'boolean') {
        params.push({ key: 'groupValue', value: this.group.label });
      }
    }
    if (this.tagsGroupSubscr) {
      this.tagsGroupSubscr.unsubscribe();
    }

    /* Have to make two calls (for true & false) for groupByValue booleans
      as they are grouped under "" (noname) header */
    if (this.group.label === 'boolean') {
      params.push({ key: 'groupValue', value: 'false' });
      this.getTagLogsByGroup(params, body, pushList);
      const groupValueIndex = params.findIndex((e) => e.key === 'groupValue');
      params.splice(groupValueIndex, 1);
      params.push({ key: 'groupValue', value: 'true' });
      this.getTagLogsByGroup(params, body, pushList);
    } else {
      this.getTagLogsByGroup(params, body, pushList);
    }
  }

  getTagLogsByGroup(params: any[], body: GraphTagSearch, pushList: boolean) {
    this.tagsGroupSubscr = this.stationRepo
      .getStationGroupedTagSearch(
        this.data.siteId,
        this.offset,
        this.limit,
        params,
        body
      )
      .subscribe({
        next: (tagsGroupPagedList: GraphTagGroupListModel) => {
          if (pushList) {
            const newList = this.tagsList.list.concat(tagsGroupPagedList.list);
            this.tagsList.paging = tagsGroupPagedList.paging;
            this.tagsList.list = newList.slice();
            this.tagsList.list = this.tagsList.list.slice();
            this.tagsList = this.tagsList;
          } else {
            this.tagsList = tagsGroupPagedList;
          }

          if (!this.firstPass) {
            this.showOrHide();
            this.firstPass = true;
          }

          const event = new EventsUpdateTagGroup();
          event.hasGetNewListOfGroup = true;
          setTimeout(() => {
            this.eventOnClick.next(event);
          }, 1000);
        },
        error: (error) => {
          const alertParams = new AlertParameters(
            false,
            false,
            true,
            false,
            false,
            false,
            true,
            'Erreur de récupération d\'événements'
          );
          this.alertService.showAlert(error, AlertType.ERROR, alertParams);
        },
      });
  }

  showMore() {
    this.offset = this.offset + this.limit;
    this.getTagsGroupByField(true);
  }

  onEventOnClick(event: any) {
    this.eventOnClick.emit(event);
  }
}
